import type { RegisteredSkill } from '../types'
import { runAddCommentReplies, runInsertComments, runInsertHtml } from './handlers/write-handlers'

export const WRITE_SKILLS: RegisteredSkill[] = [
  {
    name: 'insert_html',
    description:
      "Insert sanitized HTML at Word's current caret/selection. location before/after/replace adds, appends, or rewrites relative to that anchor (default replace). Always uses Word Track Changes. Use read skills first to find the right place and style. Mutates the document.",
    argsSchema: {
      type: 'object',
      properties: {
        html: { type: 'string', description: 'HTML to insert' },
        location: {
          type: 'string',
          enum: ['before', 'after', 'replace'],
          description:
            'Insert relative to the current Word selection (default replace; table routing applies only to replace)',
        },
      },
      required: ['html'],
      additionalProperties: false,
    },
    resultSchema: {
      type: 'object',
      properties: {
        ok: { type: 'boolean', const: true },
      },
      required: ['ok'],
    },
    handler: runInsertHtml,
  },
  {
    name: 'insert_comments',
    description:
      'Insert new anchored Word comments. Each comment needs paragraph_id from get_document_paragraphs, get_section with per_paragraph true, or get_selected_paragraphs for selection-scoped work. By default paragraph_id is resolved anywhere in the document (resolve_document_wide true). For selection-only anchoring, set resolve_document_wide false and pass selection_paragraph_ids from get_selected_paragraphs (live selection used if omitted). skill_result.ok is false only when inserted_count is 0; partial batches still return ok true — scan comment_ids (parallel to input) for null skipped items, re-read ids and retry once. Mutates the document.',
    argsSchema: {
      type: 'object',
      properties: {
        comments: {
          type: 'array',
          minItems: 1,
          description: 'New comments to insert (at least one)',
          items: {
            type: 'object',
            properties: {
              paragraph_id: {
                type: 'string',
                description: 'Anchor paragraph uniqueLocalId from a read skill',
              },
              comment_text: { type: 'string', description: 'Comment body' },
              search_text: {
                type: ['string', 'null'],
                description: 'Optional exact words inside the paragraph to attach to',
              },
            },
            required: ['paragraph_id', 'comment_text'],
            additionalProperties: false,
          },
        },
        selection_paragraph_ids: {
          type: 'array',
          items: { type: 'string' },
          description:
            'Paragraph ids from get_selected_paragraphs when scoping inserts to the current selection. Ignored when resolve_document_wide is true.',
        },
        resolve_document_wide: {
          type: 'boolean',
          description:
            'When true (default), resolve paragraph_id anywhere in the document. When false, scope to selection_paragraph_ids. Prefer false when inserting to the live selection.',
        },
      },
      required: ['comments'],
      additionalProperties: false,
    },
    resultSchema: {
      type: 'object',
      description:
        'Partial success is ok true when inserted_count > 0; check comment_ids for null even on success.',
      properties: {
        inserted_count: { type: 'number' },
        comment_ids: {
          type: 'array',
          description:
            'Parallel to input comments; Word comment id when anchored, null when that item was skipped',
          items: { type: ['string', 'null'] },
        },
      },
      required: ['inserted_count', 'comment_ids'],
    },
    handler: runInsertComments,
  },
  {
    name: 'add_comment_replies',
    description:
      'Reply to existing Word comment threads. Use comment_id values from get_full_document_comments (reads also include paragraph_id for context). skill_result.ok is false only when inserted_count is 0; partial batches still return ok true — scan comment_ids (parallel to input) for null skipped replies, re-read ids and retry once. Mutates the document.',
    argsSchema: {
      type: 'object',
      properties: {
        replies: {
          type: 'array',
          minItems: 1,
          description: 'Replies to existing comment threads (at least one)',
          items: {
            type: 'object',
            properties: {
              comment_id: {
                type: 'string',
                description: 'Existing Word comment thread id',
              },
              comment_text: { type: 'string', description: 'Reply body' },
            },
            required: ['comment_id', 'comment_text'],
            additionalProperties: false,
          },
        },
      },
      required: ['replies'],
      additionalProperties: false,
    },
    resultSchema: {
      type: 'object',
      description:
        'Partial success is ok true when inserted_count > 0; check comment_ids for null even on success.',
      properties: {
        inserted_count: { type: 'number' },
        comment_ids: {
          type: 'array',
          description:
            'Parallel to input replies; thread comment_id when the reply was added, null when skipped',
          items: { type: ['string', 'null'] },
        },
      },
      required: ['inserted_count', 'comment_ids'],
    },
    handler: runAddCommentReplies,
  },
]
